import { Stack, type SxProps, type Theme } from '@mui/material';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { BLOCKQUOTE_SX, MarkdownBlockquote } from './Blockquote.js';
import { MarkdownLink } from './Link.js';
import { MarkdownListItem, MarkdownOl, MarkdownUl } from './List.js';
import { MarkdownTable } from './Table.js';
import { MarkdownText } from './Text.js';
import { useMarkdownContext } from './context.js';
import { transformMarkdownUrl } from './urlTransform.js';
import { mergeSxProps } from '../Mui/mergeSxProps';

/**
 * Uses lookbehind and Unicode property escapes, which are not supported in some browsers (notably older Safari versions).
 *
 * @returns True if the browser supports the regex, false otherwise.
 */
function doesBrowserSupportRemarkGfmAutolinkRegex(): boolean {
  try {
    // This pattern is used by `mdast-util-gfm-autolink-literal` (used by `remark-gfm`) to detect
    // email addresses in plain text (so `foo@example.com` becomes a link) only
    // when they are preceded by a "boundary" character (start of string, whitespace, punctuation, or symbol).
    // See: https://github.com/syntax-tree/mdast-util-gfm-autolink-literal/blob/ba83f42e2d6e7bafbb213b2bf94ec533891a6d82/lib/index.js#L135
    // eslint-disable-next-line prefer-regex-literals
    const re = new RegExp('(?<=^|\\s|\\p{P}|\\p{S})([-.\\w+]+)@([\\w-]+(?:\\.[\\w-]+)+)', 'gu');
    void re;
    return true;
  } catch {
    return false;
  }
}

interface NotableMarkdownProps {
  children: string;
  /**
   * The gap between components in MUI Spacing units.
   * Default is 2 for most components, and 1 for h4-h6.
   */
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
}

export function NotableMarkdown(props: NotableMarkdownProps) {
  const { children, gap = 2 } = props;
  const { extraComponents, extraRemarkPlugins } = useMarkdownContext();

  const remarkPlugins = [
    ...(doesBrowserSupportRemarkGfmAutolinkRegex() ? [remarkGfm] : []),
    ...(Array.isArray(extraRemarkPlugins) ? extraRemarkPlugins : []),
  ];
  return (
    <Stack gap={gap}>
      <Markdown
        remarkPlugins={remarkPlugins}
        urlTransform={transformMarkdownUrl}
        components={{
          ...extraComponents,
          h1: (props) => <MarkdownText {...props} variant="heading2" bold />,
          h2: (props) => <MarkdownText {...props} variant="heading3" bold />,
          h3: (props) => <MarkdownText {...props} variant="heading4" bold />,
          h4: (props) => <MarkdownText {...props} variant="heading5" bold />,
          h5: (props) => <MarkdownText {...props} variant="heading6" bold />,
          h6: (props) => <MarkdownText {...props} variant="heading6" />,
          p: (props) => <MarkdownText {...props} variant="body2" />,
          ul: (props) => <MarkdownUl {...props} />,
          ol: (props) => <MarkdownOl {...props} />,
          li: (props) => <MarkdownListItem {...props} />,
          table: (props) => <MarkdownTable {...props} />,
          a: (props) => <MarkdownLink {...props} />,
          blockquote: (props) => <MarkdownBlockquote {...props} />,
        }}
      >
        {children}
      </Markdown>
    </Stack>
  );
}

export const MUI_MARKDOWN_TYPOGRAPHY_SX_PROPS: SxProps<Theme> = mergeSxProps(
  {
    '& h1': (theme: Theme) => theme.typography.h2,
    '& h2': (theme: Theme) => theme.typography.h3,
    '& h3': (theme: Theme) => theme.typography.h4,
    '& h4': (theme: Theme) => theme.typography.h5,
    '& h5': (theme: Theme) => theme.typography.h6,
    '& h6': (theme: Theme) => theme.typography.h6,
    '& p': (theme: Theme) => theme.typography.body2,
  },
  {
    '& blockquote': BLOCKQUOTE_SX as any,
  }
);
